import React, { useRef, useState } from 'react';
import { motion, AnimatePresence, useScroll, useSpring } from 'motion/react';
import Reveal from '../ui/Reveal';
import SectionHeader from '../ui/SectionHeader';
import './Experience.css';

const EASE = [0.22, 1, 0.36, 1];

const ROLES = [
  {
    id: 'ottovate',
    company: 'Ottovate',
    role: 'AI Engineer Intern',
    period: 'July 2026 – now',
    type: 'current',
    typeLabel: 'Current',
    summary: 'Deepening expertise in Vision-Language Models (VLMs) and deep learning architectures to extend detection systems beyond bounding-box classification toward contextual scene understanding. Conducting comparative technical research across YOLO model generations to evaluate architectural trade-offs.',
  },
  {
    id: 'psh',
    company: 'PSH Educational Technologies',
    role: 'AI Engineer & Product Owner',
    period: 'Oct 2025 – Jul 2026',
    type: 'part-time',
    typeLabel: 'Part-time',
    summary: 'Designed and developed "Hızlı Okuyo," a fast-reading education platform. Fine-tuned NLP models to automatically generate reading comprehension questions from texts, creating a dynamic assessment system. Managed the technical integration for the B2B partnership, enabling seamless user onboarding and data synchronization.',
  },
  {
    id: 'curiosity',
    company: 'Curiosity Technology',
    role: 'AI Developer Intern',
    period: 'Summer 2025',
    type: 'internship',
    typeLabel: 'Internship',
    summary: 'Developed and implemented AI-driven features for ZEKAI v2, improving system performance. Built and iterated on Fitty, an AI-powered dietitian assistant mobile app, to enhance user engagement.',
  },
  {
    id: 'autoliv',
    company: 'Autoliv Turkey',
    role: 'IT Department Intern',
    period: 'Summer 2024',
    type: 'internship',
    typeLabel: 'Internship',
    summary: 'Gained experience in monitoring production systems and understanding data flow in manufacturing processes. Diagnosed and resolved software-related issues, improving IT efficiency. Participated in global IT communication workflows. Installed and maintained computer hardware systems in a production environment.',
  },
  {
    id: 'toyota',
    company: 'Toyota Turkey Sales and Marketing Inc.',
    role: 'Digital Solutions Department Intern',
    period: 'Summer 2023',
    type: 'internship',
    typeLabel: 'Internship',
    summary: 'Created and optimized complex SQL queries for data retrieval and analysis. Assisted in internal tool development using C# and .NET technologies. Gained insight into corporate communication and digital transformation processes.',
  },
];

const Experience = () => {
  const [open, setOpen] = useState(ROLES[0].id);
  const listRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: listRef, offset: ['start 78%', 'end 60%'] });
  const drawn = useSpring(scrollYProgress, { stiffness: 90, damping: 22 });

  return (
    <section id="experience" className="section exp">
      <div className="container">
        <SectionHeader eyebrow="work" title="Where I've shipped." lede="Tap a role to expand it." />

        <div className="exp-list" ref={listRef}>
          <div className="exp-rail" aria-hidden="true">
            <motion.div className="exp-rail-fg" style={{ scaleY: drawn }} />
          </div>

          {ROLES.map((r, i) => {
            const isOpen = open === r.id;
            return (
              <Reveal key={r.id} delay={i * 0.05} className={`exp-row ${isOpen ? 'is-open' : ''}`}>
                <motion.span
                  className="exp-dot"
                  initial={{ scale: 0.5, backgroundColor: '#CBD2DE' }}
                  whileInView={{ scale: 1, backgroundColor: '#7C5CFF' }}
                  viewport={{ once: true, margin: '0px 0px -40% 0px' }}
                  transition={{ duration: 0.5, ease: EASE }}
                  aria-hidden="true"
                />
                <button
                  type="button"
                  className="exp-head"
                  onClick={() => setOpen(isOpen ? null : r.id)}
                  aria-expanded={isOpen}
                  aria-controls={`exp-panel-${r.id}`}
                >
                  <span className="exp-period mono">{r.period}</span>
                  <span className="exp-main">
                    <span className="exp-company t-display">{r.company}</span>
                    <span className="exp-role">{r.role}</span>
                  </span>
                  <span className="exp-meta">
                    <span className={`tag exp-type exp-type--${r.type}`}>{r.typeLabel}</span>
                    <motion.span
                      className="exp-plus"
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                      aria-hidden="true"
                    >
                      +
                    </motion.span>
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="panel"
                      id={`exp-panel-${r.id}`}
                      className="exp-panel"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.45, ease: EASE }}
                    >
                      <p>{r.summary}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Experience;
